/* ============================================================
   storage — the viewer, on its own.

   The preview pane shares a window with the file list, which is
   right for stepping through a folder and wrong for actually
   watching something. This is the same player with the rest of
   the app taken away, opened from a URL that names the file and
   the second it should start at:

     /?file=<id>&t=<seconds>&viewer=1

   Which is the same scheme the pane inside the browser uses —
   `viewer=1` is the whole difference. So it survives being sent
   to someone, a second tab can play one clip while the first
   keeps browsing, and a refresh comes back to the same second.
   ============================================================ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { Icon, fileIconName, fmtBytes, fmtDate, fileUrl } from './lib';
import MediaPlayer from './MediaPlayer';
import { previewKind } from './Preview';
import { useStreams, qualitiesFrom, streamNote } from './streams';
import { useDirectSrc } from './direct';
import { rememberOpened } from './Home';

export default function Viewer({
  token, fileId, startAt, me, onDownload, onShowInFolder, onExit, onPosition, onMissing, trim, reloadKey, directFor,
}) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [failed, setFailed] = useState(false);
  // The second the player is on, for the link the Copy button writes.
  const atRef = useRef(startAt || 0);
  // Held in a ref so that a caller passing a fresh arrow every render cannot
  // turn the fetch below into a loop.
  const missingRef = useRef(onMissing);
  missingRef.current = onMissing;

  useEffect(() => {
    if (!token || !fileId) return undefined;
    const ctrl = new AbortController();
    setError(''); setFailed(false);
    // A reload of the same file (its bytes were trimmed in place) keeps the
    // page up while the new row arrives; only another file starts blank.
    setFile((f) => (f && f.id === fileId ? f : null));
    api.file(token, fileId, ctrl.signal)
      .then((f) => { setFile(f); rememberOpened(f); })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.status === 403
          ? 'That file is inside a secure folder that is not yours to open.'
          : (e.status === 404 ? 'That file is gone.' : (e.message || 'Could not open that file')));
        missingRef.current?.(e.status === 403);
      });
    return () => ctrl.abort();
  }, [token, fileId, reloadKey]);

  useEffect(() => {
    document.title = file ? `${file.original_name} — storage` : 'Viewer — storage';
  }, [file]);

  const mark = useCallback((seconds, force, key) => {
    atRef.current = seconds;
    onPosition?.(seconds, force, key);
  }, [onPosition]);

  const isVideo = !!file && previewKind(file) === 'video';
  const playable0 = !!file && ['video', 'audio'].includes(previewKind(file));
  const media = useDirectSrc(token, file, file ? directFor?.(file.drive) : null,
    file ? api.inlineUrl(token, file.id, file.sha256) : null, playable0);
  const streams = useStreams(isVideo ? () => api.fileStreams(token, file.id) : null,
    isVideo ? `${file.id}:${file.sha256 || ''}` : null);

  const copyLink = useCallback(() => {
    navigator.clipboard?.writeText(fileUrl(fileId, atRef.current)).catch(() => {});
  }, [fileId]);

  if (error) {
    return (
      <div className="st-viewer">
        <div className="st-viewer-empty">
          <Icon name="warn" size={40}/>
          <p className="meta">{error}</p>
          <button type="button" className="btn btn--sm" onClick={onExit}>Back to storage</button>
        </div>
      </div>
    );
  }
  if (!file) {
    return (
      <div className="st-viewer">
        <div className="st-viewer-empty"><span className="skeleton st-viewer-skel"/></div>
      </div>
    );
  }

  const kind = previewKind(file);
  const playable = kind === 'video' || kind === 'audio';
  const src = media.src;

  return (
    <div className="st-viewer">
      <header className="st-viewer-head">
        <Icon name={fileIconName(file.mime_type, file.original_name)} size={16} className="st-icon-file"/>
        <h1 className="st-viewer-name" title={file.original_name}>{file.original_name}</h1>
        <span className="st-viewer-facts tnum">
          {fmtBytes(file.size)} · {file.owner_id === me?.id ? 'you' : file.owner_name || '—'} · {fmtDate(file.created_at)}
        </span>
        <div className="st-viewer-acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick={copyLink}>
            <Icon name="share" size={14}/> Copy link
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
                  onClick={() => onShowInFolder(file)}>
            <Icon name="folder" size={14}/> Show in folder
          </button>
          <button type="button" className="btn btn--sm" onClick={() => onDownload(file)}>
            <Icon name="download" size={14}/> Download
          </button>
        </div>
      </header>

      <div className="st-viewer-stage">
        {playable && !failed && !src ? (
          <div className="st-viewer-empty"><span className="skeleton st-viewer-skel"/></div>
        ) : playable && !failed ? (
          <MediaPlayer
            key={`${file.id}:${file.sha256 || ''}`}
            src={src}
            name={file.original_name}
            kind={kind}
            mediaKey={file.id}
            token={token}
            startAt={startAt}
            onPosition={mark}
            onError={() => setFailed(true)}
            onDownload={() => onDownload(file)}
            qualities={streams ? qualitiesFrom(streams,
              (q) => (media.direct && streams.direct?.[q]) || api.streamCopyUrl(token, file.id, q), src) : undefined}
            qualityNote={streamNote(streams)}
            trim={kind === 'video' ? trim : undefined}
            file={file}
          />
        ) : (
          <div className="st-viewer-empty">
            <Icon name={fileIconName(file.mime_type, file.original_name)} size={44}/>
            <p className="meta">
              {failed ? 'This browser could not play this file.' : 'This file is not something the viewer can play.'}
            </p>
            <button type="button" className="btn btn--sm" onClick={() => onDownload(file)}>
              <Icon name="download" size={14}/> Download
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
